<script>
import useService from '../../../../Composables/Service/useService.ts'
import useServiceForm from '../../../../Composables/Service/useServiceForm.ts'
import mixinComponent from '../../Mixins/mixinComponent'
import Alert from '../UI/Alert.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import Opteven from './External/Opteven.vue'
import ServiceLevelForm from './ServiceLevelForm.vue'

export default {
  components: { Alert, ServiceLevelForm, ButtonAll, FormCreator, FieldCreator, ModalDialog, opteven: Opteven },
  mixins: [mixinComponent],
  props: {
    title: { type: String, required: true },
    serviceId: { type: [Number, null], required: false, default: null },
    serviceTypeId: { type: [Number, null], required: false, default: null },
    financeId: { type: Number, required: true },
    duration: { type: Number, required: true },
  },
  data: () => ({
    loading: true,
    sending: false,
    formData: {},
    formSubmitDisabled: false,
  }),
  computed: {
    hasLevel() {
      if (null === this.service) {
        return false
      }

      return this.service.serviceType.level === this.service.level
    },
    hasApi() {
      return !!this.service.others && !!this.service.others.api
    },
    modalId() {
      return `service-${this.serviceId}`
    },
    params() {
      const params = {
        financeId: this.financeId,
      }

      if (this.serviceId) {
        params['id'] = this.serviceId
      } else if (this.serviceTypeId) {
        params['serviceTypeId'] = this.serviceTypeId
      }

      return params
    },
    apiFieldComponent() {
      if (this.hasApi) {
        return this.uncapitalize(this.service.others.api.label)
      }

      return null
    },
  },
  methods: {
    updateData(data) {
      this.formData = data
    },
    open() {
      this.$refs.dialog.open()
    },
    close() {
      this.$refs.dialog.close()
    },
    onFormBuild({ loading, object, def }) {
      this.loading = loading
      this.setService(object)
      this.setDef(def)
    },
    async onFormSubmitted(updatedService) {
      this.setService(updatedService)
      this.$refs.myformservice.setObject(updatedService)
      this.$emit('update', updatedService)

      if (!this.serviceId) {
        await this.$nextTick()
        await this.$refs.myformservice.methods('get') // Force def refresh
      }
    },
    setDef(def) {
      this.def = def
    },
    setService(service) {
      this.service = service
    },
    computeMajoration(majoration) {
      const increasedPrice = this.$root.getRoundFloat(this.service.amountBase * (1 + majoration / 100), 1, 100)

      this.service.amount = increasedPrice
    },
    toggleSending(sending) {
      this.sending = sending
    },
    uncapitalize(string) {
      return string.charAt(0).toLowerCase() + string.slice(1)
    },
    updateFormSubmit(values) {
      if (values.disabled !== undefined) {
        this.formSubmitDisabled = values.disabled
      }
    },
    updateService(values) {
      Object.entries(values).forEach(([key, value]) => {
        this.$refs.myformservice.datas[key] = value
      })
    },
    submit() {
      this.$refs.myformservice.methods()
    },
  },
  setup() {
    const { service } = useService()
    const { def, hasIncreaseRateField } = useServiceForm({ service: service })

    return { def, hasIncreaseRateField, service }
  },
  mounted() {
    this.open()
  },
  watch: {
    'service.level': {
      handler(value) {
        if (!this.formData) {
          return
        }

        if (!value) {
          delete this.formData.datas?.levels
        }
      },
    },
  },
}
</script>
<template>
  <ModalDialog
    ref="dialog"
    :id="modalId"
    class-modal-dialog="modal-xl"
    :title="title"
    e2e-modal="service"
    @icon-close="$emit('close')"
  >
    <div v-if="loading" class="row justify-content-center">
      <div class="lds-ellipsis">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>
    <template v-else>
      <FieldCreator
        v-if="!serviceId"
        :form-data="formData"
        name="serviceType"
        form-ref="myformservice"
        domain="service"
        label-top
      />
      <FieldCreator :form-data="formData" name="equipment" form-ref="myformservice" domain="service" label-top />
      <FieldCreator :form-data="formData" name="supplier" form-ref="myformservice" domain="service" label-top />
      <FieldCreator
        :form-data="formData"
        v-if="!isTheme('yneia')"
        name="contractNumber"
        form-ref="myformservice"
        domain="service"
        label-top
      />
      <FieldCreator :form-data="formData" name="amountBase" form-ref="myformservice" domain="service" label-top />
      <FieldCreator
        :form-data="formData"
        name="majoration"
        form-ref="myformservice"
        domain="service"
        label-top
        @get-input-change="computeMajoration"
      />
      <div class="row">
        <div v-if="!service.level" class="col">
          <FieldCreator
            :form-data="formData"
            name="amount"
            form-ref="myformservice"
            domain="service"
            label-top
            not-remove
          />
        </div>

        <div v-if="hasIncreaseRateField" class="col">
          <FieldCreator
            v-model.number="service.increaseRate"
            :form-data="formData"
            name="increaseRate"
            form-ref="myformservice"
            domain="service"
            label-top
          />
        </div>
      </div>
      <FieldCreator :form-data="formData" name="includeToRent" form-ref="myformservice" domain="service" label-top />
      <FieldCreator
        v-if="!isTheme('yneia')"
        v-model="service.level"
        :form-data="formData"
        name="level"
        form-ref="myformservice"
        domain="service"
        label-top
      />

      <ServiceLevelForm v-if="service.level" :service="service" :form="formData" />

      <template v-if="def?.others">
        <template v-for="name in Object.keys(def.others)" :key="name">
          <FieldCreator
            :form-data="formData"
            :name="'others.' + name"
            form-ref="myformservice"
            domain="service"
            :placeholder-config="false"
            :refresh-field="true"
            label-top
            dynamic-label
          />
        </template>
      </template>
      <template v-if="hasApi">
        <component
          :is="apiFieldComponent"
          :service="service"
          :duration="duration"
          @update:service="updateService"
          @update:form-submit="updateFormSubmit"
        />
      </template>
    </template>
    <div class="my-3">
      <FormCreator
        @update-data="updateData"
        ref="myformservice"
        endpoint="services"
        :id="serviceId"
        :method="serviceId ? 'patch' : 'post'"
        :params="params"
        in-modal
        class-to-add="d-none"
        @global-build="onFormBuild"
        @form-return="onFormSubmitted"
        @is-sending="toggleSending"
        @def-build="setDef"
        @refresh-return="setService"
      />

      <ButtonAll
        type="validation"
        size="large"
        margin="no"
        label="button.validate"
        :disabled="formSubmitDisabled"
        :loading="sending"
        @click="submit"
      />
      <ButtonAll type="secondary" size="large" margin="no" label="main.close" class="ml-3" @click="close" />
    </div>
  </ModalDialog>
</template>
